// Blocks datastream panel: the click-expanded detail of the live strip +
// leader track region, rendered below the plot in the same zone as the
// income drill-down (one detail surface at a time — the host enforces it).
//
// Content, top to bottom: the running epoch's live summary sentence
// (phase 3, operator direction — the line the main-view strip used to
// carry; the host builds its segments and refreshes them on the shared
// poll and on countdown ticks while the panel is open), the epoch's
// production-quality line (/skips), and the stream table — the epoch's
// produced blocks NEWEST FIRST, slot / time (UTC HH:MM) / fees (SOL) /
// tips (SOL). Fees and time come from the /schedule?epoch slots timeline;
// tips join from the complete /blocks epoch fetch. Every figure is a
// served fact or a written absence:
// - tips for a block absent from a COMPLETE /blocks response render
//   "not yet crawled" (a measured absence — the fetch is complete-or-error),
//   never 0;
// - before that response lands, tips render the em dash (unknown, not an
//   absence claim about the daemon);
// - a null served skip rate renders "skip rate not yet measurable".
//
// Close grammar mirrors drilldown.ts: [x] (icons.ts closeIcon) and Escape
// close; focus returns to the trigger that opened the panel.

import { closeIcon } from "./icons";
import type { ScheduleSlot, SkipsRow } from "./api";
import { formatCount, formatUtcHourMinute, lamportsToSol } from "./format";
import { renderLiveStrip, type LivePart } from "./live-strip";
import { rowButtonSemantics } from "./income-explorer-views";

/** Visible row cap (V1: no pagination); older blocks fold into the trailing count line. */
export const STREAM_ROW_CAP = 20;

export const TIPS_NOT_CRAWLED_TEXT = "not yet crawled";
export const SKIP_RATE_UNMEASURABLE_TEXT = "skip rate not yet measurable";
export const QUALITY_PENDING_TEXT = "production quality not yet fetched";
export const QUALITY_ABSENT_TEXT = "no production-quality row served for this epoch";
export const TIMELINE_PENDING_TEXT = "leader timeline not yet fetched for this epoch";
export const NO_BLOCKS_TEXT = "no blocks produced yet this epoch";

/** One poll-time view of everything the panel renders. */
export interface StreamSnapshot {
  epoch: number;
  /** The served /schedule?epoch timeline for THIS epoch; null until it lands. */
  slots: readonly ScheduleSlot[] | null;
  /** fetched=false until a /skips response for this epoch lands; row=null then, or when no row covers the epoch. */
  quality: { fetched: boolean; row: SkipsRow | null };
  /** slot -> tips_lamports from the complete /blocks epoch fetch; null until it lands. */
  tipsBySlot: ReadonlyMap<number, number> | null;
  /**
   * The running epoch's live summary sentence (live-strip.ts
   * buildLiveSegments), rendered as the panel's first line; null renders
   * no line. Deliberately OUTSIDE streamFingerprint: countdown ticks
   * update this line in place without rebuilding the table.
   */
  liveLine: LivePart[][] | null;
}

export interface StreamRow {
  slot: number;
  blockTime: number | null;
  feeRewardLamports: number | null;
}

/** The stream never shows a block the daemon has not served: rows are exactly the status="produced" slots, newest first. */
export function producedNewestFirst(slots: readonly ScheduleSlot[]): StreamRow[] {
  return slots
    .filter((s) => s.status === "produced")
    .sort((a, b) => b.slot - a.slot)
    .map((s) => ({
      slot: s.slot,
      blockTime: s.block_time,
      feeRewardLamports: s.fee_reward_lamports,
    }));
}

/** One quality-line segment; value=null renders the label alone (the written-absence form). */
export interface QualitySegment {
  label: string;
  value: string | null;
}

/**
 * Served skip rate to display text. One decimal, except a real nonzero
 * rate that would round to "0.0%" states the honest bound "< 0.1%" —
 * a display rounding must never understate a measured failure rate.
 */
export function skipRateValueText(pct: number): string {
  if (pct > 0 && pct < 0.05) {
    return "< 0.1%";
  }
  return `${pct.toFixed(1)}%`;
}

/**
 * The epoch quality line: produced / pending / skipped always; abandoned
 * and unavailable only when nonzero (zero noise suppression — but a
 * nonzero failure count is never hidden); the skip rate last, with null
 * rendering its written absence.
 */
export function qualitySegments(row: SkipsRow): QualitySegment[] {
  const segments: QualitySegment[] = [
    { label: "produced", value: formatCount(row.produced) },
    { label: "pending", value: formatCount(row.pending) },
    { label: "skipped", value: formatCount(row.skipped) },
  ];
  if (row.abandoned !== null && row.abandoned > 0) {
    segments.push({ label: "abandoned", value: formatCount(row.abandoned) });
  }
  if (row.unavailable !== null && row.unavailable > 0) {
    segments.push({ label: "unavailable", value: formatCount(row.unavailable) });
  }
  if (row.skip_rate_pct === null) {
    segments.push({ label: SKIP_RATE_UNMEASURABLE_TEXT, value: null });
  } else {
    segments.push({ label: "skip rate", value: skipRateValueText(row.skip_rate_pct) });
  }
  return segments;
}

/**
 * Tips cell text for one produced slot. Map null = the /blocks fetch has
 * not landed: em dash (unknown). Map present without the slot = the
 * complete epoch fetch measured its absence: "not yet crawled". A served
 * 0 renders "0" — measured zero, not absence.
 */
export function tipsCellText(slot: number, tipsBySlot: ReadonlyMap<number, number> | null): string {
  if (tipsBySlot === null) {
    return "—";
  }
  const tips = tipsBySlot.get(slot);
  return tips === undefined ? TIPS_NOT_CRAWLED_TEXT : lamportsToSol(tips);
}

/**
 * Change detector for the shared-poll refresh: block production is
 * append-only and served figures for a stored block are immutable, so
 * epoch + row count + newest slot + tips coverage + the quality row
 * identify a rendered state. Equal fingerprints skip the re-render (a
 * 30 s countdown tick must not rebuild the table under the reader).
 * liveLine is excluded by design: it changes every countdown tick and
 * refreshes in place (see refresh).
 */
export function streamFingerprint(s: StreamSnapshot): string {
  const rows = s.slots === null ? null : producedNewestFirst(s.slots);
  return JSON.stringify([
    s.epoch,
    rows === null ? -1 : rows.length,
    rows?.[0]?.slot ?? -1,
    s.quality.fetched,
    s.quality.row,
    s.tipsBySlot === null ? -1 : s.tipsBySlot.size,
  ]);
}

export interface StreamCallbacks {
  /** Row activation: open the income explorer at this block. */
  onBlockSelect: (epoch: number, slot: number) => void;
}

export class BlocksStreamPanel {
  readonly #container: HTMLElement;
  readonly #callbacks: StreamCallbacks;
  readonly #onVisibility: ((open: boolean) => void) | undefined;
  #returnFocusTo: HTMLElement | null = null;
  #open = false;
  #fingerprint = "";

  constructor(container: HTMLElement, callbacks: StreamCallbacks, onVisibility?: (open: boolean) => void) {
    this.#container = container;
    this.#callbacks = callbacks;
    this.#onVisibility = onVisibility;
  }

  get isOpen(): boolean {
    return this.#open;
  }

  /** Idempotent; safe as a defensive close from exclusivity paths. */
  hide(): void {
    if (!this.#open) {
      return;
    }
    this.#open = false;
    this.#fingerprint = "";
    this.#container.replaceChildren();
    this.#container.classList.add("vf-hidden");
    this.#onVisibility?.(false);
  }

  #hideAndReturnFocus(): void {
    this.hide();
    this.#returnFocusTo?.focus({ preventScroll: true });
  }

  show(snapshot: StreamSnapshot): void {
    // Trigger capture through the shadow root (drilldown.ts precedent):
    // document.activeElement resolves to the host from outside an open
    // shadow root, not the focused descendant.
    const root = this.#container.getRootNode();
    this.#returnFocusTo =
      root instanceof ShadowRoot
        ? (root.activeElement as HTMLElement | null)
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
    this.#open = true;
    this.#render(snapshot);
    this.#container.classList.remove("vf-hidden");
    this.#onVisibility?.(true);
    // No preventScroll on open (WCAG 2.4.11; drilldown.ts note): the
    // trigger can sit a viewport away from the panel on small embeds.
    (this.#container.firstElementChild as HTMLElement | null)?.focus();
  }

  /**
   * Shared-poll refresh while open. The live summary line always updates
   * in place (it counts down every tick); unchanged snapshots
   * (fingerprint) then skip the table rebuild. When content did change
   * and focus was inside the panel, focus restores to the same slot row
   * (or the panel) rather than being dropped to the document.
   */
  refresh(snapshot: StreamSnapshot): void {
    if (!this.#open) {
      return;
    }
    if (streamFingerprint(snapshot) === this.#fingerprint) {
      const summary = this.#container.querySelector<HTMLElement>(".vf-stream-summary");
      if (summary !== null) {
        renderLiveStrip(summary, snapshot.liveLine ?? []);
      }
      return;
    }
    const root = this.#container.getRootNode();
    const active = root instanceof ShadowRoot ? (root.activeElement as HTMLElement | null) : null;
    const inside = active !== null && this.#container.contains(active);
    const focusSlot = inside ? (active.closest("tr[data-slot]")?.getAttribute("data-slot") ?? null) : null;
    const wasClose = inside && active.classList.contains("vf-close");
    this.#render(snapshot);
    if (!inside) {
      return;
    }
    const panel = this.#container.firstElementChild as HTMLElement | null;
    if (panel === null) {
      return;
    }
    if (wasClose) {
      panel.querySelector<HTMLElement>("button.vf-close")?.focus({ preventScroll: true });
      return;
    }
    const row =
      focusSlot === null ? null : panel.querySelector<HTMLElement>(`tr[data-slot="${focusSlot}"]`);
    (row ?? panel).focus({ preventScroll: true });
  }

  #render(snapshot: StreamSnapshot): void {
    this.#fingerprint = streamFingerprint(snapshot);

    const panel = document.createElement("div");
    panel.className = "vf-stream";
    panel.tabIndex = -1;
    // Persistent filtered listener (drilldown.ts: never { once: true }).
    panel.addEventListener("keydown", (ev) => {
      if (ev.key === "Escape") {
        this.#hideAndReturnFocus();
      }
    });

    const head = document.createElement("div");
    head.className = "vf-stream-head";
    const title = document.createElement("span");
    title.className = "vf-stream-title";
    title.textContent = `Epoch ${snapshot.epoch} blocks datastream`;
    const close = document.createElement("button");
    close.type = "button";
    close.className = "vf-btn";
    close.classList.add("vf-close");
    close.append(closeIcon());
    close.setAttribute("aria-label", "close blocks datastream");
    close.addEventListener("click", () => this.#hideAndReturnFocus());
    head.append(title, close);
    // First line: the running epoch's live summary sentence (phase 3 —
    // moved here from the main-view strip), above the quality row.
    const summary = document.createElement("div");
    summary.className = "vf-stream-summary";
    renderLiveStrip(summary, snapshot.liveLine ?? []);
    panel.append(head, summary, this.#qualityLine(snapshot));

    if (snapshot.slots === null) {
      panel.append(note(TIMELINE_PENDING_TEXT));
    } else {
      const rows = producedNewestFirst(snapshot.slots);
      if (rows.length === 0) {
        panel.append(note(NO_BLOCKS_TEXT));
      } else {
        panel.append(this.#table(snapshot, rows));
        if (rows.length > STREAM_ROW_CAP) {
          const more = document.createElement("div");
          more.className = "vf-stream-more";
          more.textContent = `and ${formatCount(rows.length - STREAM_ROW_CAP)} earlier blocks this epoch`;
          panel.append(more);
        }
      }
    }

    this.#container.replaceChildren(panel);
  }

  #qualityLine(snapshot: StreamSnapshot): HTMLDivElement {
    const el = document.createElement("div");
    el.className = "vf-stream-quality";
    if (!snapshot.quality.fetched) {
      el.append(span("vf-live-label", QUALITY_PENDING_TEXT));
      return el;
    }
    if (snapshot.quality.row === null) {
      el.append(span("vf-live-label", QUALITY_ABSENT_TEXT));
      return el;
    }
    const segments = qualitySegments(snapshot.quality.row);
    for (const [i, segment] of segments.entries()) {
      if (i > 0) {
        const sep = span("vf-live-sep", "·");
        sep.setAttribute("aria-hidden", "true");
        el.append(sep);
      }
      el.append(span("vf-live-label", segment.label));
      if (segment.value !== null) {
        el.append(" ", span("vf-live-num vf-mono", segment.value));
      }
    }
    return el;
  }

  #table(snapshot: StreamSnapshot, rows: readonly StreamRow[]): HTMLTableElement {
    const table = document.createElement("table");
    const thead = document.createElement("thead");
    const hr = document.createElement("tr");
    const headers: { label: string; numeric: boolean }[] = [
      { label: "slot", numeric: true },
      { label: "time (UTC)", numeric: false },
      { label: "fees (SOL)", numeric: true },
      { label: "tips (SOL)", numeric: true },
    ];
    for (const h of headers) {
      const th = document.createElement("th");
      th.textContent = h.label;
      if (h.numeric) {
        th.classList.add("vf-num");
      }
      hr.append(th);
    }
    thead.append(hr);
    table.append(thead);

    const tbody = document.createElement("tbody");
    for (const row of rows.slice(0, STREAM_ROW_CAP)) {
      const tr = document.createElement("tr");
      tr.className = "vf-stream-row";
      tr.setAttribute("data-slot", String(row.slot));
      tr.setAttribute("aria-label", `open block ${row.slot} in the income explorer`);
      tr.append(
        cell(formatCount(row.slot), true),
        cell(formatUtcHourMinute(row.blockTime), false),
        cell(lamportsToSol(row.feeRewardLamports), true),
        cell(tipsCellText(row.slot, snapshot.tipsBySlot), true),
      );
      rowButtonSemantics(tr, () => this.#callbacks.onBlockSelect(snapshot.epoch, row.slot));
      tbody.append(tr);
    }
    table.append(tbody);
    return table;
  }
}

function span(cls: string, text: string): HTMLSpanElement {
  const el = document.createElement("span");
  el.className = cls;
  el.textContent = text;
  return el;
}

function note(text: string): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "vf-stream-note";
  el.textContent = text;
  return el;
}

function cell(text: string, numeric: boolean): HTMLTableCellElement {
  const td = document.createElement("td");
  td.classList.add("vf-mono");
  if (numeric) {
    td.classList.add("vf-num");
  }
  td.textContent = text;
  return td;
}
